import { Disclosure, Transition } from "@headlessui/react";
import clsx from "clsx";
import { useMemo } from "react";

import { useWorkspaceActiveCustomFields } from "@web-app/api";
import { Icon, Tooltip } from "@web-app/components";

import {
  ENUM_VALUES,
  SQL_COLUMNS,
  SQL_MEASURES,
  customFieldSlug,
} from "./SQLEditor";

// Group columns by category for display (these are the raw database fields)
const COLUMN_CATEGORIES: Record<string, { columns: string[]; label: string }> =
  {
    actual_times: {
      columns: [
        "in_transit",
        "pickup_arrival_time",
        "actual_pickup_time",
        "dropoff_arrival_time",
        "actual_dropoff_time",
      ],
      label: "Actual Times",
    },
    assignment: {
      columns: [
        "assignee",
        "assignee_id",
        "route_id",
        "route_name",
        "index_in_route",
      ],
      label: "Assignment & Route",
    },
    distance: {
      columns: ["distance", "distance_covered"],
      label: "Distance",
    },
    dropoff: {
      columns: [
        "dropoff_address",
        "dropoff_state",
        "recipient_name",
        "recipient_phone",
        "recipient_business_name",
        "recipient_note",
      ],
      label: "Dropoff Details",
    },
    financial: {
      columns: ["value", "tip"],
      label: "Financial",
    },
    order_details: {
      columns: [
        "order_id",
        "external_id",
        "delivery_type",
        "status",
        "priority",
        "description",
        "items_count",
        "service_time",
        "vehicle_type",
      ],
      label: "Order Details",
    },
    pickup: {
      columns: [
        "pickup_address",
        "pickup_state",
        "pickup_name",
        "pickup_phone",
        "pickup_business_name",
        "pickup_note",
      ],
      label: "Pickup Details",
    },
    requirements: {
      columns: [
        "signature_requirement",
        "proof_of_delivery_requirement",
        "notes_requirement",
        "pickup_signature_requirement",
        "pickup_proof_of_pickup_requirement",
        "pickup_notes_requirement",
      ],
      label: "Requirements",
    },
    scheduled_times: {
      columns: ["scheduled_start_time", "scheduled_end_time", "estimated_eta"],
      label: "Scheduled Times",
    },
    status_details: {
      columns: ["status_notes", "status_reasons"],
      label: "Status Details",
    },
    system: {
      columns: ["id", "created_at", "updated_at", "timezone", "metadata"],
      label: "System",
    },
  };

// Group measures by category for display (these are calculated/aggregated values)
const MEASURE_CATEGORIES: Record<
  string,
  { label: string; measures: string[] }
> = {
  counts: {
    label: "Counts",
    measures: [
      "count",
      "completed_count",
      "failed_count",
      "cancelled_count",
      "in_progress_count",
      "pickup_only_count",
      "dropoff_only_count",
      "pickup_and_dropoff_count",
    ],
  },
  distance: {
    label: "Distance",
    measures: ["total_distance_covered", "avg_distance_covered"],
  },
  financial: {
    label: "Financial Totals",
    measures: [
      "total_value",
      "avg_value",
      "total_tip",
      "avg_tip",
      "total_revenue",
    ],
  },
  items: {
    label: "Items",
    measures: ["total_items", "avg_items_per_order"],
  },
  other_counts: {
    label: "Other Counts",
    measures: [
      "signature_required_count",
      "proof_of_delivery_required_count",
      "routed_orders_count",
      "unrouted_orders_count",
    ],
  },
  otif: {
    label: "OTIF (On-Time In-Full)",
    measures: [
      "pickup_otif_rate",
      "dropoff_otif_rate",
      "pickup_on_time_count",
      "pickup_late_count",
      "dropoff_on_time_count",
      "dropoff_late_count",
      "pickup_late_rate",
      "dropoff_late_rate",
      "completion_rate",
    ],
  },
  time: {
    label: "Time Metrics",
    measures: [
      "total_service_time",
      "avg_service_time",
      "avg_pickup_to_dropoff_duration",
      "avg_scheduled_to_start_delay",
      "avg_total_order_duration",
    ],
  },
};

// Tables synced for analytics. Each table owns its columns/measures/enum
// values, so the schema tree reads as Table > Fields. Add new entries here
// as more tables get synced.
const TABLES = [
  {
    columnCategories: COLUMN_CATEGORIES,
    enumValues: ENUM_VALUES,
    measureCategories: MEASURE_CATEGORIES,
    name: "orders",
  },
];

interface SchemaReferenceProps {
  onInsert: (text: string) => void;
}

export default function SchemaReference({ onInsert }: SchemaReferenceProps) {
  // Fetch custom fields from all connected workspaces
  const { customFields } = useWorkspaceActiveCustomFields();

  // Transform custom fields to match schema format
  const transformedCustomFields = useMemo(() => {
    if (!customFields || customFields.length === 0) return [];
    return customFields.map((cf) => ({
      description: cf.name, // Original name as description
      name: customFieldSlug(cf.name), // Just the slug for UI
      type: cf.type, // Keep the type for reference
    }));
  }, [customFields]);

  // Create a lookup map for column descriptions
  const columnDescriptions = SQL_COLUMNS.reduce(
    (acc, col) => {
      acc[col.name] = col.description;
      return acc;
    },
    {} as Record<string, string>,
  );

  const handleInsert = (text: string) => {
    onInsert(text);
  };

  return (
    <div className="h-full overflow-y-auto bg-gray-50">
      <div className="p-3">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
          Schema Reference
        </h3>

        {/* Tables Section - each table nests its own Columns/Measures/Values */}
        {TABLES.map((table) => (
          <Disclosure key={table.name} defaultOpen>
            {({ open }) => (
              <div className="mb-2">
                <Disclosure.Button
                  as="div"
                  className="flex w-full cursor-pointer items-center justify-between rounded-md bg-white px-3 py-2 text-left text-sm font-medium text-gray-700 hover:bg-gray-100 focus:outline-none"
                >
                  <span className="flex items-center gap-2">
                    <span className="flex h-5 w-5 items-center justify-center rounded bg-amber-100 text-amber-700 text-xs font-medium">
                      T
                    </span>
                    <span className="font-mono text-xs text-gray-700">
                      {table.name}
                    </span>
                  </span>
                  <span className="flex items-center gap-2">
                    <button
                      type="button"
                      title={`Insert ${table.name}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleInsert(table.name);
                      }}
                      className="text-gray-300 hover:text-blue-500"
                    >
                      <Icon name="plus" className="h-3 w-3" />
                    </button>
                    <Icon
                      name={open ? "fasChevronDown" : "fasChevronRight"}
                      className="h-3 w-3 text-gray-400"
                    />
                  </span>
                </Disclosure.Button>
                <Transition
                  enter="transition duration-100 ease-out"
                  enterFrom="transform opacity-0"
                  enterTo="transform opacity-100"
                  leave="transition duration-75 ease-out"
                  leaveFrom="transform opacity-100"
                  leaveTo="transform opacity-0"
                >
                  <Disclosure.Panel className="mt-1 space-y-1 pl-3">
                    {/* Columns Section - Raw database fields for filtering/grouping */}
                    <Disclosure defaultOpen>
                      {({ open: columnsOpen }) => (
                        <div className="mb-2">
                          <Disclosure.Button className="flex w-full items-center justify-between rounded-md bg-white px-3 py-2 text-left text-sm font-medium text-gray-700 hover:bg-gray-100 focus:outline-none">
                            <span>
                              Columns (
                              {SQL_COLUMNS.length +
                                transformedCustomFields.length}
                              )
                            </span>
                            <Icon
                              name={
                                columnsOpen
                                  ? "fasChevronDown"
                                  : "fasChevronRight"
                              }
                              className="h-3 w-3 text-gray-400"
                            />
                          </Disclosure.Button>
                          <Transition
                            enter="transition duration-100 ease-out"
                            enterFrom="transform opacity-0"
                            enterTo="transform opacity-100"
                            leave="transition duration-75 ease-out"
                            leaveFrom="transform opacity-100"
                            leaveTo="transform opacity-0"
                          >
                            <Disclosure.Panel className="mt-1 space-y-2 pl-2">
                              {Object.entries(table.columnCategories).map(
                                ([categoryKey, { label, columns }]) => (
                                  <div key={categoryKey}>
                                    <div className="text-xs font-medium text-gray-400 uppercase px-2 py-1">
                                      {label}
                                    </div>
                                    <div className="space-y-0.5">
                                      {columns.map((columnName) => (
                                        <Tooltip
                                          key={columnName}
                                          text={`${columnName}${columnDescriptions[columnName] ? ` - ${columnDescriptions[columnName]}` : ""}`}
                                          position="left"
                                          showIcon={false}
                                        >
                                          <button
                                            type="button"
                                            onClick={() =>
                                              handleInsert(columnName)
                                            }
                                            className="flex w-full items-center gap-2 rounded px-2 py-1 text-left text-sm hover:bg-white group"
                                          >
                                            <span className="flex h-4 w-4 items-center justify-center rounded bg-green-100 text-green-700 text-xs font-medium flex-shrink-0">
                                              C
                                            </span>
                                            <span className="font-mono text-xs text-gray-600 group-hover:text-blue-600 truncate">
                                              {columnName}
                                            </span>
                                            <Icon
                                              name="plus"
                                              className="h-3 w-3 text-gray-300 group-hover:text-blue-500 ml-auto flex-shrink-0"
                                            />
                                          </button>
                                        </Tooltip>
                                      ))}
                                    </div>
                                  </div>
                                ),
                              )}

                              {/* Custom Fields Section */}
                              {transformedCustomFields.length > 0 && (
                                <div>
                                  <div className="text-xs font-medium text-gray-400 uppercase px-2 py-1">
                                    Custom Fields
                                  </div>
                                  <div className="space-y-0.5">
                                    {transformedCustomFields.map(
                                      (customField) => (
                                        <Tooltip
                                          key={customField.name}
                                          text={`${customField.name} - ${customField.description} (${customField.type})`}
                                          position="left"
                                          showIcon={false}
                                        >
                                          <button
                                            type="button"
                                            onClick={() =>
                                              handleInsert(customField.name)
                                            }
                                            className="flex w-full items-center gap-2 rounded px-2 py-1 text-left text-sm hover:bg-white group"
                                          >
                                            <span className="flex h-4 w-4 items-center justify-center rounded bg-purple-100 text-purple-700 text-xs font-medium flex-shrink-0">
                                              CF
                                            </span>
                                            <span className="font-mono text-xs text-gray-600 group-hover:text-blue-600 truncate">
                                              {customField.name}
                                            </span>
                                            <Icon
                                              name="plus"
                                              className="h-3 w-3 text-gray-300 group-hover:text-blue-500 ml-auto flex-shrink-0"
                                            />
                                          </button>
                                        </Tooltip>
                                      ),
                                    )}
                                  </div>
                                </div>
                              )}
                            </Disclosure.Panel>
                          </Transition>
                        </div>
                      )}
                    </Disclosure>

                    {/* Measures Section - Calculated/aggregated values from Cube.js */}
                    <Disclosure defaultOpen>
                      {({ open: measuresOpen }) => (
                        <div className="mb-2">
                          <Disclosure.Button className="flex w-full items-center justify-between rounded-md bg-white px-3 py-2 text-left text-sm font-medium text-gray-700 hover:bg-gray-100 focus:outline-none">
                            <span>Measures ({SQL_MEASURES.length})</span>
                            <Icon
                              name={
                                measuresOpen
                                  ? "fasChevronDown"
                                  : "fasChevronRight"
                              }
                              className="h-3 w-3 text-gray-400"
                            />
                          </Disclosure.Button>
                          <Transition
                            enter="transition duration-100 ease-out"
                            enterFrom="transform opacity-0"
                            enterTo="transform opacity-100"
                            leave="transition duration-75 ease-out"
                            leaveFrom="transform opacity-100"
                            leaveTo="transform opacity-0"
                          >
                            <Disclosure.Panel className="mt-1 space-y-2 pl-2">
                              {Object.entries(table.measureCategories).map(
                                ([categoryKey, { label, measures }]) => (
                                  <div key={categoryKey}>
                                    <div className="text-xs font-medium text-gray-400 uppercase px-2 py-1">
                                      {label}
                                    </div>
                                    <div className="space-y-0.5">
                                      {measures.map((measureName) => {
                                        const measure = SQL_MEASURES.find(
                                          (m) => m.name === measureName,
                                        );
                                        return (
                                          <Tooltip
                                            key={measureName}
                                            text={`${measureName}${measure?.description ? ` - ${measure.description}` : ""}`}
                                            position="left"
                                            showIcon={false}
                                          >
                                            <button
                                              type="button"
                                              onClick={() =>
                                                handleInsert(measureName)
                                              }
                                              className="flex w-full items-center gap-2 rounded px-2 py-1 text-left text-sm hover:bg-white group"
                                            >
                                              <span className="flex h-4 w-4 items-center justify-center rounded bg-blue-100 text-blue-700 text-xs font-medium flex-shrink-0">
                                                M
                                              </span>
                                              <span className="font-mono text-xs text-gray-600 group-hover:text-blue-600 truncate">
                                                {measureName}
                                              </span>
                                              <Icon
                                                name="plus"
                                                className="h-3 w-3 text-gray-300 group-hover:text-blue-500 ml-auto flex-shrink-0"
                                              />
                                            </button>
                                          </Tooltip>
                                        );
                                      })}
                                    </div>
                                  </div>
                                ),
                              )}
                            </Disclosure.Panel>
                          </Transition>
                        </div>
                      )}
                    </Disclosure>

                    {/* Values Section - Enum values scoped to this table */}
                    <Disclosure defaultOpen>
                      {({ open: valuesOpen }) => (
                        <div className="mb-2">
                          <Disclosure.Button className="flex w-full items-center justify-between rounded-md bg-white px-3 py-2 text-left text-sm font-medium text-gray-700 hover:bg-gray-100 focus:outline-none">
                            <span>Values</span>
                            <Icon
                              name={
                                valuesOpen
                                  ? "fasChevronDown"
                                  : "fasChevronRight"
                              }
                              className="h-3 w-3 text-gray-400"
                            />
                          </Disclosure.Button>
                          <Transition
                            enter="transition duration-100 ease-out"
                            enterFrom="transform opacity-0"
                            enterTo="transform opacity-100"
                            leave="transition duration-75 ease-out"
                            leaveFrom="transform opacity-100"
                            leaveTo="transform opacity-0"
                          >
                            <Disclosure.Panel className="mt-1 space-y-2 pl-2">
                              {Object.entries(table.enumValues).map(
                                ([field, values]) => (
                                  <div key={field}>
                                    <div className="text-xs font-medium text-gray-400 uppercase px-2 py-1">
                                      {field.replace(/_/g, " ")}
                                    </div>
                                    <div className="space-y-0.5">
                                      {values.map((enumVal) => (
                                        <Tooltip
                                          key={enumVal.value}
                                          text={`${enumVal.label} → ${enumVal.value}`}
                                          position="left"
                                          showIcon={false}
                                        >
                                          <button
                                            type="button"
                                            onClick={() =>
                                              handleInsert(enumVal.value)
                                            }
                                            className="flex w-full items-center gap-2 rounded px-2 py-1 text-left text-sm hover:bg-white group"
                                          >
                                            <span className="flex h-4 w-4 items-center justify-center rounded bg-pink-100 text-pink-700 text-xs font-medium flex-shrink-0">
                                              E
                                            </span>
                                            <span className="font-mono text-xs text-gray-600 group-hover:text-blue-600 truncate">
                                              {enumVal.label}
                                            </span>
                                            <Icon
                                              name="plus"
                                              className="h-3 w-3 text-gray-300 group-hover:text-blue-500 ml-auto flex-shrink-0"
                                            />
                                          </button>
                                        </Tooltip>
                                      ))}
                                    </div>
                                  </div>
                                ),
                              )}
                            </Disclosure.Panel>
                          </Transition>
                        </div>
                      )}
                    </Disclosure>
                  </Disclosure.Panel>
                </Transition>
              </div>
            )}
          </Disclosure>
        ))}

        {/* SQL Keywords Section - not tied to any table */}
        <Disclosure>
          {({ open }) => (
            <div className="mb-2">
              <Disclosure.Button className="flex w-full items-center justify-between rounded-md bg-white px-3 py-2 text-left text-sm font-medium text-gray-700 hover:bg-gray-100 focus:outline-none">
                <span>SQL Keywords</span>
                <Icon
                  name={open ? "fasChevronDown" : "fasChevronRight"}
                  className="h-3 w-3 text-gray-400"
                />
              </Disclosure.Button>
              <Transition
                enter="transition duration-100 ease-out"
                enterFrom="transform opacity-0"
                enterTo="transform opacity-100"
                leave="transition duration-75 ease-out"
                leaveFrom="transform opacity-100"
                leaveTo="transform opacity-0"
              >
                <Disclosure.Panel className="mt-1 pl-2">
                  <div className="flex flex-wrap gap-1 p-2">
                    {[
                      "SELECT",
                      "FROM",
                      "WHERE",
                      "GROUP BY",
                      "ORDER BY",
                      "LIMIT",
                      "COUNT",
                      "SUM",
                      "AVG",
                      "MIN",
                      "MAX",
                      "AND",
                      "OR",
                      "AS",
                      "IN",
                    ].map((keyword) => (
                      <button
                        key={keyword}
                        type="button"
                        onClick={() => handleInsert(keyword + " ")}
                        className={clsx(
                          "rounded px-2 py-0.5 text-xs font-mono",
                          "bg-gray-100 text-gray-600 hover:bg-blue-100 hover:text-blue-700",
                        )}
                      >
                        {keyword}
                      </button>
                    ))}
                  </div>
                </Disclosure.Panel>
              </Transition>
            </div>
          )}
        </Disclosure>
      </div>
    </div>
  );
}
